// Return from Stripe Checkout: `?checkout=success|cancel` on /rapor/<token>.
// Pure helpers plus a banner; the page wires the timers and URL clean-up.
import { CheckCircle2, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const RETURN_POLL_DELAYS_MS = [3000, 8000];

export function parseCheckoutReturn(search) {
  try {
    const v = new URLSearchParams(search || "").get("checkout");
    return v === "success" || v === "cancel" ? v : null;
  } catch {
    return null;
  }
}

// Drops only the `checkout` param; other params and the hash stay.
export function stripCheckoutParam(href) {
  try {
    const u = new URL(href);
    u.searchParams.delete("checkout");
    return u.pathname + (u.search || "") + u.hash;
  } catch {
    return href;
  }
}

export const COPY = {
  de: {
    reportPaid: "Zahlung eingegangen. Wir bereiten Ihren ausführlichen Bericht persönlich vor und senden ihn Ihnen per E-Mail.",
    consultingActive: "Ihre Beratung ist freigeschaltet.",
    consultingPending: "Freischaltung läuft, bitte Seite in einem Moment neu laden",
    checking: "Wir prüfen Ihre Zahlung …",
    cancelled: "Zahlung abgebrochen",
    cancelledBody: "Es wurde nichts abgebucht. Sie können den Vorgang jederzeit erneut starten.",
    retry: "Erneut versuchen",
    prepTitle: "Bericht in Vorbereitung",
    prepBody: "Zahlung erhalten. Wir bereiten Ihren ausführlichen Bericht persönlich vor und senden ihn Ihnen per E-Mail.",
  },
  en: {
    reportPaid: "Payment received. We are preparing your detailed report personally and will send it to you by e-mail.",
    consultingActive: "Your consulting is unlocked.",
    consultingPending: "Activation in progress, please reload the page in a moment",
    checking: "We are checking your payment …",
    cancelled: "Payment cancelled",
    cancelledBody: "Nothing was charged. You can start again at any time.",
    retry: "Try again",
    prepTitle: "Report in preparation",
    prepBody: "Payment received. We are preparing your detailed report personally and will send it to you by e-mail.",
  },
};

// What the banner says. On a paid (detailed) report a purchase can only have
// been the consulting product; on the free report it was the report.
export function returnBannerModel(state, data, lang = "de") {
  const C = COPY[lang] || COPY.de;
  if (state === "cancel") return { kind: "cancel", title: C.cancelled, body: C.cancelledBody, retry: C.retry };
  if (state !== "success") return null;
  if (data && data.is_detailed) {
    const active = !!(data.consulting && data.consulting.active);
    return active
      ? { kind: "success", title: C.consultingActive }
      : { kind: "pending", title: C.consultingPending };
  }
  if (data && data.purchase_status === "paid") return { kind: "success", title: C.reportPaid };
  return { kind: "pending", title: C.checking };
}

// Free report already paid for but not yet turned into the detailed one.
export const isPreparing = (data) => !!data && data.purchase_status === "paid" && !data.is_detailed;

export function ReturnBanner({ state, data, lang, onRetry }) {
  const m = returnBannerModel(state, data, lang);
  if (!m) return null;
  const Icon = m.kind === "success" ? CheckCircle2 : m.kind === "pending" ? Loader2 : Info;
  return (
    <div className="mx-auto max-w-3xl px-4 pt-4 print:hidden" data-testid="checkout-return">
      <div
        role="status"
        className={`flex items-start gap-3 rounded-xl border p-4 text-base ${
          m.kind === "success" ? "border-green-300 bg-green-50" : "bg-card"
        }`}
      >
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${m.kind === "pending" ? "animate-spin" : ""} ${m.kind === "success" ? "text-green-700" : "text-muted-foreground"}`} />
        <div className="min-w-0">
          <p className="font-medium">{m.title}</p>
          {m.body && <p className="mt-0.5 text-muted-foreground">{m.body}</p>}
          {m.retry && onRetry && (
            <Button size="sm" variant="outline" className="mt-2" onClick={onRetry} data-testid="checkout-retry">{m.retry}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function PreparingCard({ lang }) {
  const C = COPY[lang] || COPY.de;
  return (
    <div id="angebot" className="scroll-mt-4 rounded-2xl border border-green-300 bg-green-50 p-5 sm:p-6 print:hidden" data-testid="offer-preparing">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-green-700" />
        <div>
          <h2 className="font-heading text-xl font-bold">{C.prepTitle}</h2>
          <p className="mt-1 text-base">{C.prepBody}</p>
        </div>
      </div>
    </div>
  );
}
